// ─────────────────────────────────────────────────────────────────────────────
// T239 — THE TELEMETRY POLICY. One module, two consumers.
//
// This file is imported by BOTH ends of the pipe:
//   · the server  — supabase/functions/collect-events/handler.ts (the only writer)
//   · the client  — src/lib/telemetry.ts (the only sender)
//
// That is deliberate and it is the whole point of the file. The event whitelist,
// the prop-key whitelist and the text scrubber are the three things that decide
// what may EXIST in the table. If the client carried its own copy, the two would
// drift the first time somebody added an event on one side only — and the drift
// would be invisible, because the server would simply drop what the client sent
// and the funnel would quietly under-count. One list, one drop rule, no drift.
//
// CONSEQUENCES OF BEING IMPORTED FROM `src/` (the T228 fixture rule):
// runtime-dependency-free — no `Deno.*`, no `jsr:` / `npm:` specifier, not even a
// type import, because `tsc --noEmit` and vite must both resolve this file when
// the client pulls it in. Pure TypeScript, pure functions, no clock, no I/O.
//
// ── THE PRIVACY POSTURE THIS FILE ENFORCES (card §红线, 零 PII) ───────────────
// The privacy page's sentence ("first-party, minimal, no IP addresses") is a
// CONTRACT, and this module is where it is kept, in three layers:
//   1. WHITELIST, not blacklist. An event name not in EVENTS is dropped. A prop
//      key not in PROP_SPECS is dropped. Nothing arrives by being "not banned";
//      it arrives by being named here, in a file a reviewer reads in one screen.
//   2. TYPED VALUES. Every allowed key declares a type and a bound. A string
//      where a number belongs is dropped, not coerced — coercion is how a
//      free-text field sneaks in behind an innocuous name.
//   3. SCRUB ANYWAY. Even the values that pass 1 and 2 go through `scrubText`,
//      which removes URL query strings (tokens live there), address-shaped text
//      and long digit runs. Layer 3 exists because layers 1 and 2 are decided by
//      whoever edits this file next, and they may be less careful than the
//      person reading it now.
// What is NEVER accepted, at any layer: an IP address, a user-agent string, a
// name, an email, a phone number, a free-text answer. The server additionally
// never READS the request's IP or UA headers at all — see handler.ts.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * THE TEN EVENTS. Exactly this list, in the migration's CHECK constraint, in the
 * client's `emit()` type and here. Adding an eleventh takes a migration, so the
 * table can never hold an event nobody declared.
 *
 * Keep byte-identical to `telemetry_events.event`'s CHECK in
 * `supabase/migrations/20260729020000_t239_telemetry_events.sql`. The fixture
 * asserts the two lists match (a drift lock, not a promise).
 */
export const EVENTS = [
  "page_view",
  "finder_start",
  "finder_step",
  "finder_complete",
  "finder_report_submit",
  "booking_start",
  "booking_payment_open",
  "booking_paid_return",
  "js_error",
  "web_vital",
] as const;

export type TelemetryEvent = (typeof EVENTS)[number];

const EVENT_SET: ReadonlySet<string> = new Set(EVENTS);

export function isAllowedEvent(name: unknown): name is TelemetryEvent {
  return typeof name === "string" && EVENT_SET.has(name);
}

// ── Bounds. Every one of these numbers is also asserted by the fixture against
//    the place it is mirrored (SQL CHECK / client), so a value changed here and
//    nowhere else is a red test, not a silent truncation in production. ────────

/** Max events accepted per request. Extra entries are dropped, not 400'd. */
export const MAX_BATCH = 20;
/** Max prop keys kept per event, after the whitelist. */
export const MAX_PROPS_KEYS = 8;
/** Serialized `props` ceiling — mirrors the migration's pg_column_size CHECK. */
export const MAX_PROPS_BYTES = 2048;
/** `path` ceiling — mirrors the migration's char_length CHECK. */
export const MAX_PATH = 200;
/** js_error message/stack ceiling (card §2: 截 500 字符). */
export const MAX_ERROR_TEXT = 500;
/** session_hash shape: 32 lowercase hex chars, mirrored by the SQL CHECK. */
export const SESSION_HASH_RE = /^[0-9a-f]{32}$/;
/** The two locales the site renders. */
export const LANGS = ["en", "zh"] as const;
export type Lang = (typeof LANGS)[number];

/**
 * Per-session hourly ceiling, counted from stored rows the way finder-report
 * counts its per-address window. A real session emits well under this (a page
 * view, a handful of funnel steps, the odd web vital); it is a bound on what one
 * session can put in the table, not a budget anybody legitimately spends.
 */
export const MAX_EVENTS_PER_SESSION_HOUR = 120;
export const THROTTLE_WINDOW_MS = 60 * 60 * 1000;

// ── The prop whitelist ───────────────────────────────────────────────────────

type PropType = "int" | "num" | "str";

interface PropSpec {
  type: PropType;
  /** strings: max length after scrubbing. numbers: max absolute value. */
  max: number;
  /** ints: minimum accepted value (default 0 — no negative step numbers). */
  min?: number;
  /** When set, the key is accepted ONLY on these events. */
  onlyOn?: readonly TelemetryEvent[];
}

/**
 * EVERY key that may reach the database, with its type and bound.
 *
 * Read this list as "the complete set of facts telemetry is allowed to know".
 * There is no key here that identifies a person, and there is deliberately no
 * escape hatch: no `meta`, no `extra`, no `detail` object, because a nested
 * object is a place a future edit puts an email without noticing.
 */
export const PROP_SPECS: Readonly<Record<string, PropSpec>> = {
  /** finder_step: which question index the visitor reached (1-based). */
  step: { type: "int", max: 64, min: 0 },
  /** finder_step/finder_complete: how many steps the questionnaire had. */
  total: { type: "int", max: 64, min: 0 },
  /** A COUNT, never the ids: how many pathways the deterministic engine returned. */
  pathways: { type: "int", max: 64, min: 0 },
  /** finder_report_submit / booking_*: a short outcome slug (ok, throttled, error…). */
  outcome: { type: "str", max: 32 },
  /** js_error / submit failures: a short machine code, never a sentence. */
  code: { type: "str", max: 48 },
  /** web_vital: which metric (LCP, CLS, INP, TTFB, FCP). */
  metric: { type: "str", max: 16, onlyOn: ["web_vital"] },
  /** web_vital: the measurement, rounded by the client. */
  value: { type: "num", max: 3_600_000, onlyOn: ["web_vital"] },
  /** js_error: the error message, scrubbed and truncated to MAX_ERROR_TEXT. */
  message: { type: "str", max: MAX_ERROR_TEXT, onlyOn: ["js_error"] },
  /** js_error: the first frames of the stack, scrubbed and truncated likewise. */
  stack: { type: "str", max: MAX_ERROR_TEXT, onlyOn: ["js_error"] },
};

/** Keys a caller may send. Anything else is dropped — this is the main mutation target. */
export const ALLOWED_PROP_KEYS: readonly string[] = Object.keys(PROP_SPECS);

// ── Scrubbing ────────────────────────────────────────────────────────────────

/**
 * Address-shaped text. Deliberately looser than an RFC-correct email matcher:
 * the job is not to validate addresses, it is to make sure nothing that LOOKS
 * like one survives, including the malformed ones a stack trace would carry.
 */
const EMAIL_RE = /[^\s/@]+@[^\s/@]+\.[^\s/@]+/g;
/** 7+ consecutive digits (phone numbers, client ids, anything countable). */
const LONG_DIGITS_RE = /\d{7,}/g;
/** A URL's query string and/or fragment — where tokens, emails and ids live. */
const QUERY_OR_FRAGMENT_RE = /[?#][^\s'"]*/g;

export const REDACTED = "[redacted]";

/**
 * The last line of defence, applied to EVERY string value that survives the
 * whitelist, plus to `path`.
 *
 * Order matters: query strings go first (an email inside `?email=` should be
 * removed with the whole query, not left behind as a redaction marker), then
 * addresses, then long digit runs, then whitespace collapse, then truncation.
 *
 * Truncation is last so a caller cannot use a long prefix of junk to push PII
 * past the cut and into the stored value.
 */
export function scrubText(raw: unknown, max: number): string | null {
  if (typeof raw !== "string") return null;
  const cleaned = raw
    .replace(QUERY_OR_FRAGMENT_RE, "")
    .replace(EMAIL_RE, REDACTED)
    .replace(LONG_DIGITS_RE, REDACTED)
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return null;
  return cleaned.length > max ? cleaned.slice(0, max) : cleaned;
}

/**
 * A route, reduced to the shape a funnel needs: pathname only.
 *
 * Everything after `?` or `#` is removed BEFORE anything else looks at the
 * string — the booking flow's own `?step=3` return and the campaign links the
 * site is given both live there, and neither belongs in a row we keep for 90
 * days. An absolute URL is reduced to its path (never the host, never the
 * origin's credentials); anything that is not a path at all becomes null.
 */
export function normalizePath(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  let p = raw.trim();
  if (!p) return null;
  // Absolute URL → path. Parsed by hand rather than with URL(): this module is
  // shared with the client bundle and must not depend on a global.
  const schemeAt = p.indexOf("://");
  if (schemeAt !== -1) {
    const afterHost = p.indexOf("/", schemeAt + 3);
    p = afterHost === -1 ? "/" : p.slice(afterHost);
  }
  const cut = p.search(/[?#]/);
  if (cut !== -1) p = p.slice(0, cut);
  if (!p.startsWith("/")) p = `/${p}`;
  // A path is a route, never prose: strip anything that is not a plain route
  // character so an id or an address cannot ride in a mangled segment.
  p = p.replace(/[^A-Za-z0-9/_\-.:]/g, "");
  if (p.length > MAX_PATH) p = p.slice(0, MAX_PATH);
  return p || "/";
}

export interface SanitizedProps {
  props: Record<string, string | number>;
  /** How many keys were thrown away. COUNT only — never the key names, which
   *  could echo the very thing we refused to store back to the caller. */
  dropped: number;
}

/**
 * Project a caller's props onto the whitelist. Never throws; a hostile payload
 * degrades to `{}` rather than an error.
 *
 * The rules, in order:
 *   · not an object                        → {}
 *   · key not in PROP_SPECS                → dropped   ← the card's main target
 *   · key not allowed on THIS event        → dropped
 *   · value of the wrong type              → dropped (never coerced)
 *   · number out of range / not finite     → dropped
 *   · string → scrubbed; empty after that  → dropped
 *   · more than MAX_PROPS_KEYS survivors   → the extras dropped
 *   · serialized result over MAX_PROPS_BYTES → keys removed until it fits
 */
export function sanitizeProps(event: string, raw: unknown): SanitizedProps {
  const out: Record<string, string | number> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { props: out, dropped: 0 };
  }
  let dropped = 0;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const spec = PROP_SPECS[key];
    if (!spec) { dropped += 1; continue; }
    if (spec.onlyOn && !spec.onlyOn.includes(event as TelemetryEvent)) { dropped += 1; continue; }
    if (Object.keys(out).length >= MAX_PROPS_KEYS) { dropped += 1; continue; }

    if (spec.type === "str") {
      const s = scrubText(value, spec.max);
      if (s === null) { dropped += 1; continue; }
      out[key] = s;
      continue;
    }
    if (typeof value !== "number" || !Number.isFinite(value)) { dropped += 1; continue; }
    if (spec.type === "int" && !Number.isInteger(value)) { dropped += 1; continue; }
    const min = spec.min ?? 0;
    if (value < min || value > spec.max) { dropped += 1; continue; }
    out[key] = spec.type === "int" ? value : Math.round(value * 1000) / 1000;
  }

  // Size ceiling, enforced by REMOVING keys rather than by truncating the JSON
  // (a truncated JSON string is not JSON, and a row that fails the SQL CHECK
  // takes the whole batch's insert with it).
  while (JSON.stringify(out).length > MAX_PROPS_BYTES) {
    const last = Object.keys(out).pop();
    if (last === undefined) break;
    delete out[last];
    dropped += 1;
  }
  return { props: out, dropped };
}

export function isLang(v: unknown): v is Lang {
  return v === "en" || v === "zh";
}

export function isSessionHash(v: unknown): v is string {
  return typeof v === "string" && SESSION_HASH_RE.test(v);
}
